import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import type { AddressBookApi } from "shared-ui/src/shared-features/address-book";
import type {
  OrgUnitAddressGroup,
  CreateAddressRequest,
  CreateAddressResponse,
  CreatePersonalAddressRequest,
  UpdateAddressRequest,
  ValidateAddressResponse,
  AddressFormValues,
  StorefrontAddress,
  CreateAddressGroupParams,
} from "shared-ui/src/shared-features/address-book";
import type {
  AddressLookupResponse,
  AddressCoordinateLookupResponse,
} from "shared-ui/src/generated/models";
import { ApiClientConfig } from "../../../../../auth/ApiClientConfig";
import { useGetAddressMap } from "../../maps-feature/hooks/useGetAddressMap";

const API_BASE = `${import.meta.env.API_ENDPOINT}/api/StorefrontAddress`;

const CxAddressQueryKeys = {
  AddressLookup: "cx-address-lookup",
  AddressCoordinates: "cx-address-coordinates",
  OrgUnitAddressGroups: "cx-org-unit-address-groups",
  StorefrontAddresses: "cx-storefront-addresses",
  PersonalAddresses: "cx-personal-addresses",
  RecentAddresses: "cx-recent-addresses",
  BrowseOrgUnitAddresses: "cx-browse-org-unit-addresses",
  BrowsePersonalAddresses: "cx-browse-personal-addresses",
} as const;

const BROWSE_PAGE_SIZE = 500;

async function cxFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const authorization = await ApiClientConfig.getAuthorization();
  const additionalHeaders = ApiClientConfig.getAdditionalHeaders();
  const headers = new Headers(additionalHeaders);
  headers.set("Authorization", authorization);
  headers.set("Accept", "application/json");

  if (init?.body) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(url, { ...init, headers });
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  return response.json();
}

interface AddressSearchItemResponse {
  streetAddressId: number;
  addressGroups: string[] | null;
  firstName: string | null;
  lastName: string | null;
  addressName: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  countryCode: string | null;
  residential: boolean;
  isDefault: boolean;
  attention?: string | null;
}

function mapToStorefrontAddress(
  dto: AddressSearchItemResponse,
): StorefrontAddress {
  return {
    id: String(dto.streetAddressId),
    siteName: dto.addressName || undefined,
    streetAddress: dto.address1 ?? "",
    streetAddress2: dto.address2 || undefined,
    city: dto.city ?? "",
    state: dto.state ?? "",
    zip: dto.postalCode ?? "",
    country: dto.countryCode || dto.country || "",
    groups: dto.addressGroups ?? [],
    isDefault: dto.isDefault,
    attention: dto.attention ?? undefined,
  };
}

function hasEnoughData(values: AddressFormValues | null): boolean {
  if (!values) return false;
  return !!(
    values.streetAddress.trim() &&
    values.city.trim() &&
    values.state.trim()
  );
}

export function useSearchPersonalAddresses(searchText: string) {
  const trimmed = searchText.trim();
  return useQuery<StorefrontAddress[], Error>({
    queryKey: [CxAddressQueryKeys.PersonalAddresses, trimmed],
    queryFn: async () => {
      const data = await cxFetch<{ addresses: AddressSearchItemResponse[] }>(
        `${API_BASE}/personal/search?searchText=${encodeURIComponent(trimmed)}&page=1&pageSize=500`,
      );
      return (data.addresses ?? []).map(mapToStorefrontAddress);
    },
    enabled: trimmed.length >= 3,
  });
}

export function useSearchOrgUnitAddresses(
  orgUnitId: number | null,
  searchText: string,
  enabled = true,
) {
  const trimmed = searchText.trim();
  return useQuery<StorefrontAddress[], Error>({
    queryKey: [CxAddressQueryKeys.StorefrontAddresses, orgUnitId, trimmed],
    queryFn: async () => {
      if (orgUnitId == null) return [];
      const data = await cxFetch<{ addresses: AddressSearchItemResponse[] }>(
        `${API_BASE}/org-unit/${orgUnitId}/search?searchText=${encodeURIComponent(trimmed)}&page=1&pageSize=500`,
      );
      return (data.addresses ?? []).map(mapToStorefrontAddress);
    },
    enabled: enabled && orgUnitId != null && trimmed.length >= 3,
  });
}

export function useRecentStorefrontAddresses(orgUnitId: number | null) {
  return useQuery<StorefrontAddress[], Error>({
    queryKey: [CxAddressQueryKeys.RecentAddresses, orgUnitId],
    queryFn: async () => {
      const params =
        orgUnitId == null ? "?count=5" : `?orgUnitId=${orgUnitId}&count=5`;
      const data = await cxFetch<{ addresses: AddressSearchItemResponse[] }>(
        `${API_BASE}/recent${params}`,
      );
      return (data.addresses ?? []).map(mapToStorefrontAddress);
    },
  });
}

interface BrowseAddressPage {
  addresses: StorefrontAddress[];
  totalCount: number;
  page: number;
}

/**
 * Full org-unit address list for the checkout browse view (no search query). Mirrors the
 * address-book page's org-unit/search retrieval and lazy-loads additional pages (500 at a
 * time) as the user scrolls, until every address (`totalCount`) has been fetched.
 */
export function useBrowseOrgUnitAddresses(
  orgUnitId: number | null,
  enabled = true,
) {
  return useInfiniteQuery<BrowseAddressPage, Error>({
    queryKey: [CxAddressQueryKeys.BrowseOrgUnitAddresses, orgUnitId],
    queryFn: async ({ pageParam = 1 }) => {
      const page = pageParam as number;
      if (orgUnitId == null) return { addresses: [], totalCount: 0, page };
      const data = await cxFetch<{
        addresses: AddressSearchItemResponse[];
        totalCount: number;
      }>(
        `${API_BASE}/org-unit/${orgUnitId}/search?searchText=&page=${page}&pageSize=${BROWSE_PAGE_SIZE}`,
      );
      return {
        addresses: (data.addresses ?? []).map(mapToStorefrontAddress),
        totalCount: data.totalCount ?? 0,
        page,
      };
    },
    getNextPageParam: (_lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.addresses.length, 0);
      const totalCount = allPages[allPages.length - 1]?.totalCount ?? 0;
      return loaded < totalCount ? allPages.length + 1 : undefined;
    },
    enabled: enabled && orgUnitId != null,
  });
}

/**
 * Full personal address list for the checkout browse view. Personal lists are small, so a
 * single page (up to 500) is sufficient and no lazy-loading is required.
 */
export function useBrowsePersonalAddresses(enabled = true) {
  return useQuery<StorefrontAddress[], Error>({
    queryKey: [CxAddressQueryKeys.BrowsePersonalAddresses],
    queryFn: async () => {
      const data = await cxFetch<{ addresses: AddressSearchItemResponse[] }>(
        `${API_BASE}/personal/search?searchText=&page=1&pageSize=${BROWSE_PAGE_SIZE}`,
      );
      return (data.addresses ?? []).map(mapToStorefrontAddress);
    },
    enabled,
  });
}

export function useCreatePersonalStorefrontAddress() {
  const queryClient = useQueryClient();
  return useMutation<
    { streetAddressId: number; isExisting: boolean },
    Error,
    CreatePersonalAddressRequest
  >({
    mutationFn: (request) =>
      cxFetch<{ streetAddressId: number; isExisting: boolean }>(
        `${API_BASE}/personal`,
        { method: "POST", body: JSON.stringify(request) },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CxAddressQueryKeys.PersonalAddresses],
      });
      queryClient.invalidateQueries({
        queryKey: [CxAddressQueryKeys.BrowsePersonalAddresses],
      });
      queryClient.invalidateQueries({
        queryKey: [CxAddressQueryKeys.RecentAddresses],
      });
    },
  });
}

export function useCreateOrgUnitStorefrontAddress(orgUnitId: number | null) {
  const queryClient = useQueryClient();
  return useMutation<CreateAddressResponse, Error, CreateAddressRequest>({
    mutationFn: async (request) => {
      if (orgUnitId == null) throw new Error("Org unit ID is required");
      return cxFetch<CreateAddressResponse>(
        `${API_BASE}/org-unit/${orgUnitId}`,
        { method: "POST", body: JSON.stringify(request) },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CxAddressQueryKeys.StorefrontAddresses],
      });
      queryClient.invalidateQueries({
        queryKey: [CxAddressQueryKeys.BrowseOrgUnitAddresses],
      });
    },
  });
}

export function createCustomerExperienceAddressBookApi(): AddressBookApi {
  return {
    useAddressLookup: (query: string) => {
      const trimmed = query.trim();
      const enabled = trimmed.length >= 3;

      return useQuery<AddressLookupResponse | undefined, Error>({
        queryKey: [CxAddressQueryKeys.AddressLookup, trimmed],
        queryFn: async () => {
          return cxFetch<AddressLookupResponse>(
            `${API_BASE}/lookup?query=${encodeURIComponent(trimmed)}`,
          );
        },
        enabled,
      });
    },

    useAddressCoordinates: (input: AddressFormValues | null, fetchId = 0) => {
      const enabled = hasEnoughData(input);

      return useQuery<AddressCoordinateLookupResponse | undefined, Error>({
        queryKey: [CxAddressQueryKeys.AddressCoordinates, input, fetchId],
        queryFn: async () => {
          if (!input) return undefined;
          return cxFetch<AddressCoordinateLookupResponse>(
            `${API_BASE}/coordinates`,
            {
              method: "POST",
              body: JSON.stringify({
                streetAddress: input.streetAddress.trim(),
                streetAddress2: input.streetAddress2?.trim() || undefined,
                city: input.city.trim(),
                state: input.state.trim(),
                zip: input.zip.trim(),
                country: input.country.trim() || "US",
              }),
            },
          );
        },
        enabled,
        staleTime: 0,
      });
    },

    useAddressMap: (latitude?: number | null, longitude?: number | null) =>
      useGetAddressMap({ latitude, longitude, width: 500, height: 200 }),

    useGetOrgUnitAddressGroups: (
      orgUnitId: string | null,
      includeEmptyGroups = true,
    ) => {
      return useQuery<OrgUnitAddressGroup[], Error>({
        queryKey: [
          CxAddressQueryKeys.OrgUnitAddressGroups,
          orgUnitId,
          includeEmptyGroups,
        ],
        queryFn: async () => {
          if (!orgUnitId) return [];
          return cxFetch<OrgUnitAddressGroup[]>(
            `${API_BASE}/org-unit/${orgUnitId}/groups?includeEmptyGroups=${includeEmptyGroups}`,
          );
        },
        enabled: Boolean(orgUnitId),
      });
    },

    useCreateOrgUnitAddressGroup: () => {
      const queryClient = useQueryClient();

      return useMutation<OrgUnitAddressGroup, Error, CreateAddressGroupParams>({
        mutationFn: async ({ orgUnitId, groupName }) => {
          return cxFetch<OrgUnitAddressGroup>(
            `${API_BASE}/org-unit/${orgUnitId}/groups`,
            {
              method: "POST",
              body: JSON.stringify({ orgUnitId, groupName }),
            },
          );
        },
        onSuccess: (_data, { orgUnitId }) => {
          queryClient.invalidateQueries({
            queryKey: [
              CxAddressQueryKeys.OrgUnitAddressGroups,
              String(orgUnitId),
            ],
          });
        },
      });
    },

    useCreateStorefrontAddress: (orgUnitId: string | null) => {
      const queryClient = useQueryClient();

      return useMutation<CreateAddressResponse, Error, CreateAddressRequest>({
        mutationFn: async (request) => {
          if (!orgUnitId) throw new Error("OrgUnit ID is required");
          return cxFetch<CreateAddressResponse>(
            `${API_BASE}/org-unit/${orgUnitId}`,
            { method: "POST", body: JSON.stringify(request) },
          );
        },
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [CxAddressQueryKeys.StorefrontAddresses],
          });
          queryClient.invalidateQueries({
            queryKey: [CxAddressQueryKeys.BrowseOrgUnitAddresses],
          });
        },
      });
    },

    useUpdateStorefrontAddress: (orgUnitId: string | null) => {
      const queryClient = useQueryClient();

      return useMutation<void, Error, UpdateAddressRequest>({
        mutationFn: async (request) => {
          if (!orgUnitId) throw new Error("OrgUnit ID is required");
          return cxFetch<void>(
            `${API_BASE}/org-unit/${orgUnitId}/${request.streetAddressId}`,
            { method: "PUT", body: JSON.stringify(request) },
          );
        },
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [CxAddressQueryKeys.StorefrontAddresses],
          });
          queryClient.invalidateQueries({
            queryKey: [CxAddressQueryKeys.BrowseOrgUnitAddresses],
          });
        },
      });
    },

    useValidateAddress: () => {
      return useMutation<ValidateAddressResponse, Error, AddressFormValues>({
        mutationFn: async (values) => {
          return cxFetch<ValidateAddressResponse>(`${API_BASE}/coordinates`, {
            method: "POST",
            body: JSON.stringify({
              streetAddress: values.streetAddress.trim(),
              streetAddress2: values.streetAddress2?.trim() || undefined,
              city: values.city.trim(),
              state: values.state.trim(),
              zip: values.zip.trim(),
              country: values.country.trim() || "US",
            }),
          });
        },
      });
    },

    useGetStorefrontAddresses: (orgUnitId: string | null) => {
      return useQuery<StorefrontAddress[], Error>({
        queryKey: [CxAddressQueryKeys.StorefrontAddresses, orgUnitId],
        queryFn: async () => {
          if (!orgUnitId) return [];
          const data = await cxFetch<{
            addresses: AddressSearchItemResponse[];
          }>(`${API_BASE}/org-unit/${orgUnitId}/search?searchText=&page=1`);
          return (data.addresses ?? []).map(mapToStorefrontAddress);
        },
        enabled: Boolean(orgUnitId),
      });
    },

    useInvalidateStorefrontAddresses: () => {
      const queryClient = useQueryClient();
      return () => {
        queryClient.invalidateQueries({
          queryKey: [CxAddressQueryKeys.StorefrontAddresses],
        });
      };
    },
  };
}

let _cxAddressBookApi: AddressBookApi | null = null;

export function getCustomerExperienceAddressBookApi(): AddressBookApi {
  if (!_cxAddressBookApi) {
    _cxAddressBookApi = createCustomerExperienceAddressBookApi();
  }
  return _cxAddressBookApi;
}
